'use client'

/**
 * CookieBanner
 * ──────────────────────────────────────────────────────────────────────────
 * Lightweight NDPA + GDPR-friendly consent banner. Shows on first visit,
 * stores the decision in the `na_cookie_consent` cookie for 180 days,
 * then goes away.
 *
 * Values written to the cookie:
 *   'accepted'  → analytics (PostHog) may load
 *   'rejected'  → analytics blocked, only strictly-necessary cookies
 *
 * Strictly-necessary cookies (na_session, cf_*, vercel_*) are always allowed
 * regardless of consent because they're required for the app to function.
 *
 * Design notes:
 *   - Fixed bottom-right card, under the main layout so it doesn't cover
 *     the landing hero.
 *   - No backdrop / modal — banners that block content are a dark pattern
 *     and most NDPA regulators accept a non-blocking banner for pure
 *     analytics consent.
 *   - If the user reloads the page and their decision is already on file
 *     the banner does NOT reappear.
 */

import { useEffect, useState } from 'react'

const COOKIE_NAME = 'na_cookie_consent'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 180 // 180 days

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const target = name + '='
  const parts = document.cookie.split(';')
  for (let p of parts) {
    p = p.trim()
    if (p.startsWith(target)) return decodeURIComponent(p.slice(target.length))
  }
  return null
}

function writeCookie(name: string, value: string) {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax`
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const existing = readCookie(COOKIE_NAME)
    if (!existing) setVisible(true)
  }, [])

  if (!visible) return null

  const accept = () => {
    writeCookie(COOKIE_NAME, 'accepted')
    setVisible(false)
    // Reload so the analytics loader picks up the new consent.
    if (typeof window !== 'undefined') window.location.reload()
  }

  const reject = () => {
    writeCookie(COOKIE_NAME, 'rejected')
    setVisible(false)
  }

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      style={{
        position: 'fixed',
        right: '16px',
        bottom: '16px',
        maxWidth: '380px',
        background: '#0f1420',
        border: '1px solid #1f2937',
        borderRadius: '14px',
        padding: '16px 18px',
        color: '#e5e7eb',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", "Plus Jakarta Sans", sans-serif',
        fontSize: '13px',
        lineHeight: 1.55,
        boxShadow: '0 20px 60px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(96,165,250,0.08)',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          fontSize: '10px',
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: '#60a5fa',
          fontWeight: 700,
          marginBottom: '8px',
        }}
      >
        Cookies
      </div>
      <p style={{ margin: '0 0 12px 0', color: '#9ca3af' }}>
        We use strictly-necessary cookies to keep you signed in. With your
        consent we also use analytics cookies to see how NairAlpha gets used
        so we can improve it. You can change your mind any time.{' '}
        <a
          href="/privacy"
          style={{ color: '#60a5fa', textDecoration: 'underline' }}
        >
          Privacy Policy
        </a>
        .
      </p>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={reject}
          style={{
            background: 'transparent',
            color: '#9ca3af',
            border: '1px solid #374151',
            padding: '8px 14px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Reject
        </button>
        <button
          type="button"
          onClick={accept}
          style={{
            background: '#3b82f6',
            color: '#fff',
            border: '1px solid #3b82f6',
            padding: '8px 14px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Accept
        </button>
      </div>
    </div>
  )
}

export default CookieBanner
